import React, { useState, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight, FiPlus, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { apiUrls, getHttpHeaders } from '../config/api';
import BookContentManager from './BookContentManager';
import { CSNode } from './tree/treeHelpers';

interface BookContentLink {
  id: number;
  structure_id: number;
  book_id: number;
  created_at: string;
  is_active: boolean;
  // Joined data
  structure_name: string;
  structure_grade: string;
  structure_subject: string;
  book_title: string;
  book_description: string;
  book_image: string;
  book_grade_name: string;
  book_subject_name: string;
}

interface ContentStructure {
  id: number;
  name: string;
  description: string;
  level: string;
  grade_id: number;
  subject_id: number;
  parent_id: number | null;
  sort_order: number;
  is_active: boolean;
}

const BookContentLinks: React.FC = () => {
  const [bookContentLinks, setBookContentLinks] = useState<BookContentLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [grades, setGrades] = useState<Array<{id: number, name: string}>>([]);
  const [subjects, setSubjects] = useState<Array<{id: number, name: string}>>([]);
  
  // BookContentManager integration
  const [showBookManager, setShowBookManager] = useState(false);
  const [contentStructures, setContentStructures] = useState<ContentStructure[]>([]);
  const [selectedStructureId, setSelectedStructureId] = useState<string>('');
  const [selectedStructureForManager, setSelectedStructureForManager] = useState<CSNode | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    fetchBookContentLinks();
    fetchGrades();
    fetchSubjects();
    fetchContentStructures();
  }, []);

  const fetchBookContentLinks = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrls.contentLibrary.contentStructures}/book-content-links`, {
        headers: getHttpHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch book content links: ${response.status}`);
      }

      const data = await response.json();
      console.log('Book content links data:', data);
      setBookContentLinks(data.links || []);
    } catch (err) {
      console.error('Error fetching book content links:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch book content links');
    } finally {
      setLoading(false);
    }
  };

  const fetchGrades = async () => {
    try {
      const response = await fetch(apiUrls.books.grades, {
        headers: getHttpHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setGrades(data.grades || []);
      }
    } catch (err) {
      console.error('Error fetching grades:', err);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await fetch(apiUrls.books.subjects, {
        headers: getHttpHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSubjects(data.subjects || []);
      }
    } catch (err) {
      console.error('Error fetching subjects:', err);
    }
  };

  const fetchContentStructures = async () => {
    try {
      console.log(`📚 Fetching content structures from: ${apiUrls.contentLibrary.contentStructures}`);
      
      const response = await fetch(apiUrls.contentLibrary.contentStructures, {
        headers: getHttpHeaders()
      });
      
      console.log(`📚 Response status: ${response.status}`);
      
      if (!response.ok) {
        console.error(`❌ Failed to fetch structures: ${response.status} ${response.statusText}`);
        setContentStructures([]);
        return;
      }
      
      const data = await response.json();
      console.log(`📚 API Response:`, data);
      
      const allStructures = data.structures || data || [];
      console.log(`📚 Extracted ${allStructures.length} total content structures`);
      
      if (allStructures.length > 0) {
        console.log(`📚 Sample structure:`, allStructures[0]);
      }
      
      // Show only top-level structures (parent_id is null, undefined, or 0)
      // This excludes nested structures like chapters, units, lessons
      const topLevelStructures = allStructures.filter((structure: any) => {
        const parentId = structure.parent_id;
        const isTopLevel = !parentId || parentId === 0 || parentId === null || parentId === undefined;
        
        if (allStructures.length <= 20) {
          // Only log individual items if there aren't too many
          console.log(`  - "${structure.name}" (ID: ${structure.id}, Level: ${structure.level}, Parent: ${parentId}) → ${isTopLevel ? '✓ INCLUDED' : '✗ EXCLUDED'}`);
        }
        
        return isTopLevel;
      });
      
      console.log(`📚 Filtered to ${topLevelStructures.length} top-level structures`);
      
      // If no top-level structures found, show all (better than empty dropdown)
      if (topLevelStructures.length === 0 && allStructures.length > 0) {
        console.warn('⚠️ No top-level structures found, showing all structures instead');
        setContentStructures(allStructures);
      } else if (topLevelStructures.length > 0) {
        setContentStructures(topLevelStructures);
      } else {
        console.error('❌ No structures available at all');
        setContentStructures([]);
      }
      
      console.log(`📚 Final contentStructures state will have ${topLevelStructures.length || allStructures.length} items`);
      
    } catch (err) {
      console.error('❌ Error fetching content structures:', err);
      setContentStructures([]);
    }
  };

  const handleOpenBookManager = () => {
    if (!selectedStructureId) {
      alert('Please select a content structure first');
      return;
    }

    const structure = contentStructures.find(s => s.id === parseInt(selectedStructureId));
    if (!structure) {
      alert('Selected structure not found');
      return;
    }

    // Check if structure has grade and subject
    if (!structure.grade_id || !structure.subject_id) {
      alert(`This content structure is missing grade or subject information.\n\nStructure: ${structure.name}\nGrade ID: ${structure.grade_id || 'Not set'}\nSubject ID: ${structure.subject_id || 'Not set'}\n\nPlease edit the structure to add grade and subject before linking books.`);
      return;
    }

    // Convert string level to number (topic=1, chapter=2, unit=3, lesson=4)
    const levelMap: { [key: string]: number } = {
      'topic': 1,
      'chapter': 2,
      'unit': 3,
      'lesson': 4
    };
    const levelNumber = levelMap[structure.level?.toLowerCase()] || 0;

    // Convert to CSNode format expected by BookContentManager
    // BookContentManager only needs basic structure info (id, name, grade_id, subject_id)
    // The complex nested structure isn't actually used for book linking
    const csNode = {
      id: structure.id,
      name: structure.name,
      description: structure.description || '',
      level: levelNumber,
      grade_id: structure.grade_id,
      subject_id: structure.subject_id,
      parent_id: structure.parent_id ?? undefined,
      sort_order: structure.sort_order,
      is_active: structure.is_active,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      children: [],
      topics: []
    };
    
    console.log('📚 Opening BookContentManager for structure:', csNode);
    setSelectedStructureForManager(csNode as any);
    setShowBookManager(true);
  };

  const handleCloseBookManager = () => {
    setShowBookManager(false);
    setSelectedStructureForManager(null);
    // Refresh the links list
    fetchBookContentLinks();
  };

  const handleRemoveLink = async (linkId: number) => {
    if (!window.confirm('Are you sure you want to remove this book-content link?')) {
      return;
    }

    try {
      const response = await fetch(`${apiUrls.contentLibrary.contentStructures}/book-content-links/${linkId}`, {
        method: 'DELETE',
        headers: getHttpHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to remove link: ${response.status}`);
      }

      // Refresh the list
      fetchBookContentLinks();
    } catch (err) {
      console.error('Error removing link:', err);
      alert('Failed to remove link. Please try again.');
    }
  };

  // Filter the links based on search and filters
  const filteredLinks = bookContentLinks.filter(link => {
    const matchesSearch = searchTerm === '' || 
      link.structure_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.book_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.book_description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesGrade = selectedGrade === '' || 
      link.structure_grade === selectedGrade ||
      link.book_grade_name === selectedGrade;
    
    const matchesSubject = selectedSubject === '' || 
      link.structure_subject === selectedSubject ||
      link.book_subject_name === selectedSubject;

    return matchesSearch && matchesGrade && matchesSubject;
  });

  // Apply sorting
  let sortedLinks = [...filteredLinks];
  if (sortColumn) {
    sortedLinks.sort((a, b) => {
      let aValue: any = '';
      let bValue: any = '';

      switch (sortColumn) {
        case 'Structure':
          aValue = (a.structure_name || '').toLowerCase();
          bValue = (b.structure_name || '').toLowerCase();
          break;
        case 'Book':
          aValue = (a.book_title || '').toLowerCase();
          bValue = (b.book_title || '').toLowerCase();
          break;
        case 'Grade':
          aValue = (a.structure_grade || '').toLowerCase();
          bValue = (b.structure_grade || '').toLowerCase();
          break;
        case 'Subject':
          aValue = (a.structure_subject || '').toLowerCase();
          bValue = (b.structure_subject || '').toLowerCase();
          break;
        case 'Created':
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
  }

  // Calculate pagination
  const totalPages = Math.ceil(sortedLinks.length / itemsPerPage);
  const safePage = currentPage > totalPages && totalPages > 0 ? 1 : currentPage;
  const startIndex = (safePage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLinks = sortedLinks.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedGrade, selectedSubject]);

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>Loading book content links...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#dc2626', marginBottom: '10px' }}>Error: {error}</div>
        <button 
          onClick={fetchBookContentLinks}
          style={{
            padding: '8px 16px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header with Pagination */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '15px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <h2 style={{ color: '#059669', margin: 0, fontSize: '24px' }}>
          📖 Book Content Links
        </h2>
        
        {/* Right side: Pagination controls */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Items per page selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>
              Show:
            </label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>per page</span>
          </div>

          {totalPages > 1 && (
            <>
              <div style={{ width: '1px', height: '24px', backgroundColor: '#e5e7eb', margin: '0 4px' }} />
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={safePage === 1}
                style={{
                  padding: '8px 16px',
                  backgroundColor: safePage === 1 ? '#e5e7eb' : '#059669',
                  color: safePage === 1 ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: safePage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s'
                }}
              >
                <FiChevronLeft size={16} />
                Previous
              </button>

              <div style={{ 
                padding: '8px 16px', 
                backgroundColor: 'white',
                border: '2px solid #059669',
                borderRadius: '6px',
                color: '#059669',
                fontSize: '14px',
                fontWeight: '700',
                whiteSpace: 'nowrap'
              }}>
                Page {safePage} of {totalPages}
              </div>

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                style={{
                  padding: '8px 16px',
                  backgroundColor: safePage === totalPages ? '#e5e7eb' : '#059669',
                  color: safePage === totalPages ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: safePage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s'
                }}
              >
                Next
                <FiChevronRight size={16} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Create Link Section */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        padding: '16px',
        backgroundColor: '#f0fdf4',
        borderRadius: '8px',
        border: '1px solid #86efac',
        alignItems: 'center'
      }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#059669' }}>
            🏗️ Select Content Structure to Link Books:
          </label>
          <select
            value={selectedStructureId}
            onChange={(e) => setSelectedStructureId(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #86efac',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: 'white'
            }}
          >
            <option value="">-- Select a Content Structure --</option>
            {contentStructures.map(structure => (
              <option key={structure.id} value={structure.id}>
                {structure.name} (Level: {structure.level})
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={handleOpenBookManager}
          disabled={!selectedStructureId}
          style={{
            backgroundColor: selectedStructureId ? '#059669' : '#9ca3af',
            color: 'white',
            border: 'none',
            padding: '10px 24px',
            borderRadius: '6px',
            cursor: selectedStructureId ? 'pointer' : 'not-allowed',
            fontSize: '14px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            marginTop: '24px'
          }}
        >
          + Link Books
        </button>
      </div>

      {/* Filters and Sort */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
        gap: '16px', 
        marginBottom: '20px',
        padding: '20px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #e5e7eb'
      }}>
        <div>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>
            Search:
          </label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by structure or book name..."
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '2px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              height: '42px',
              backgroundColor: 'white'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>
            Sort By:
          </label>
          <select
            value={sortColumn}
            onChange={(e) => {
              setSortColumn(e.target.value);
              setSortDirection('asc');
            }}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '2px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              height: '42px',
              backgroundColor: 'white'
            }}
          >
            <option value="">Default Order</option>
            <option value="Structure">Structure Name</option>
            <option value="Book">Book Title</option>
            <option value="Grade">Grade</option>
            <option value="Subject">Subject</option>
            <option value="Created">Created Date</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>
            Order:
          </label>
          <select
            value={sortDirection}
            onChange={(e) => setSortDirection(e.target.value as 'asc' | 'desc')}
            disabled={!sortColumn}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '2px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              height: '42px',
              backgroundColor: sortColumn ? 'white' : '#f3f4f6',
              cursor: sortColumn ? 'pointer' : 'not-allowed'
            }}
          >
            <option value="asc">↑ Ascending (A→Z)</option>
            <option value="desc">↓ Descending (Z→A)</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>
            Grade:
          </label>
          <select
            value={selectedGrade}
            onChange={(e) => setSelectedGrade(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '2px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              height: '42px',
              backgroundColor: 'white'
            }}
          >
            <option value="">All Grades</option>
            {grades.map(grade => (
              <option key={grade.id} value={grade.name}>
                {grade.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>
            Subject:
          </label>
          <select
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '2px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              height: '42px',
              backgroundColor: 'white'
            }}
          >
            <option value="">All Subjects</option>
            {subjects.map(subject => (
              <option key={subject.id} value={subject.name}>
                {subject.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      {filteredLinks.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '10px' }}>📖</div>
          <h3 style={{ color: '#666', marginBottom: '10px' }}>No Book Content Links Found</h3>
          <p style={{ color: '#888', margin: 0 }}>
            {searchTerm || selectedGrade || selectedSubject 
              ? 'No links match your current filters. Try adjusting your search criteria.'
              : 'No books have been linked to content structures yet. Use the Content Structure management to create links.'
            }
          </p>
        </div>
      ) : (
        <div style={{ 
          display: 'grid', 
          gap: '15px'
        }}>
          {paginatedLinks.map(link => (
            <div
              key={link.id}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '20px',
                backgroundColor: 'white',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr auto', gap: '20px', alignItems: 'start' }}>
                {/* Book Image */}
                <div style={{ width: '120px', flexShrink: 0 }}>
                  {link.book_image ? (
                    <img
                      src={link.book_image}
                      alt={link.book_title}
                      style={{
                        width: '100%',
                        height: '160px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        const fallback = img.parentElement?.querySelector('div:last-child') as HTMLElement;
                        img.style.display = 'none';
                        if (fallback) {
                          fallback.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  <div style={{
                    width: '100%',
                    height: '160px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    display: link.book_image ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    color: '#9ca3af'
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '4px' }}>📚</div>
                    <div style={{ fontSize: '12px', textAlign: 'center' }}>No Image</div>
                  </div>
                </div>

                {/* Content Structure Info */}
                <div>
                  <h4 style={{ color: '#059669', margin: '0 0 8px 0', fontSize: '16px' }}>
                    🏗️ Content Structure
                  </h4>
                  <div style={{ marginBottom: '5px' }}>
                    <strong>Name:</strong> {link.structure_name}
                  </div>
                  <div style={{ marginBottom: '5px' }}>
                    <strong>Grade:</strong> {link.structure_grade}
                  </div>
                  <div>
                    <strong>Subject:</strong> {link.structure_subject}
                  </div>
                </div>

                {/* Book Info */}
                <div>
                  <h4 style={{ color: '#3b82f6', margin: '0 0 8px 0', fontSize: '16px' }}>
                    📚 Linked Book
                  </h4>
                  <div style={{ marginBottom: '5px' }}>
                    <strong>Title:</strong> {link.book_title}
                  </div>
                  <div style={{ marginBottom: '5px' }}>
                    <strong>Grade:</strong> {link.book_grade_name}
                  </div>
                  <div style={{ marginBottom: '5px' }}>
                    <strong>Subject:</strong> {link.book_subject_name}
                  </div>
                  {link.book_description && (
                    <div style={{ 
                      marginTop: '8px',
                      padding: '8px',
                      backgroundColor: '#f3f4f6',
                      borderRadius: '4px',
                      fontSize: '14px',
                      color: '#666',
                      maxHeight: '120px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <div 
                        dangerouslySetInnerHTML={{ 
                          __html: link.book_description.length > 300 
                            ? link.book_description.substring(0, 300) + '...' 
                            : link.book_description 
                        }}
                        style={{
                          lineHeight: '1.4',
                          fontSize: '13px'
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#888', 
                    marginBottom: '10px' 
                  }}>
                    Created: {new Date(link.created_at).toLocaleDateString()}
                  </div>
                  <button
                    onClick={() => handleRemoveLink(link.id)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                    onMouseOver={(e) => {
                      (e.target as HTMLButtonElement).style.backgroundColor = '#b91c1c';
                    }}
                    onMouseOut={(e) => {
                      (e.target as HTMLButtonElement).style.backgroundColor = '#dc2626';
                    }}
                  >
                    🗑️ Remove Link
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* BookContentManager Modal */}
      <BookContentManager
        isOpen={showBookManager}
        onClose={handleCloseBookManager}
        selectedStructure={selectedStructureForManager}
      />
    </div>
  );
};

export default BookContentLinks;
